import { useState, useMemo } from "react";
import { useCardExpand } from "../../lib/cardExpand";
import { ExpandButton } from "./CardModal";
import { useDashboardSync, DIM_META, type Dimension } from "../../lib/dashboardSync";

type Visit = {
  id: string;
  date: Date;
  name: string;
  category: string;
  dimension: Dimension;
  practitioner: string;
  location: string;
  description: string;
  status: "done" | "scheduled" | "today";
  icon: string;
  emoji: string;
};

type Props = { visits: Visit[]; _expanded?: boolean };

const TODAY = new Date();
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export function ScheduleCard({ visits, _expanded }: Props) {
  const { expand } = useCardExpand();
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(TODAY.getMonth());
  const [calendarYear, setCalendarYear] = useState(TODAY.getFullYear());
  const { active, setActive } = useDashboardSync();

  // Index visits by day-of-month
  const byDay = useMemo(() => {
    const map = new Map<number, Visit[]>();
    visits.forEach((v) => {
      if (v.date.getMonth() === calendarMonth && v.date.getFullYear() === calendarYear) {
        const day = v.date.getDate();
        if (!map.has(day)) map.set(day, []);
        map.get(day)!.push(v);
      }
    });
    return map;
  }, [visits, calendarMonth, calendarYear]);

  // Week sessions (next 7 days)
  const weekVisits = useMemo(() => {
    const start = new Date(TODAY);
    start.setHours(0,0,0,0);
    const end = new Date(TODAY);
    end.setDate(end.getDate() + 7);
    return visits.filter((v) => v.date >= start && v.date <= end);
  }, [visits]);

  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
  const firstDow = new Date(calendarYear, calendarMonth, 1).getDay();
  // Adjust so Monday = 0 (week starts Monday in showcase)
  const offset = (firstDow + 6) % 7;

  return (
    <div className={_expanded ? "flex flex-col h-full" : "bg-d_card border border-d_border/20 rounded-[10px] p-3 transition-colors hover:border-d_celeste/10 h-full min-h-0 overflow-hidden flex flex-col"}>
      {!_expanded && (
        <div className="font-serif text-[17px] font-semibold mb-2.5 flex items-center justify-between tracking-[0.01em]">
          My Schedule <ExpandButton onClick={() => expand({ title: "My Schedule", render: () => <ScheduleCard visits={visits} _expanded /> })} />
        </div>
      )}

      <div className="flex gap-3 flex-1 min-h-0">
        {/* Left: calendar */}
        <div className="flex-shrink-0 flex-grow-0" style={{ flexBasis: "46%" }}>
          <div className="flex items-center justify-between mb-1.5">
            <button
              onClick={() => { const m = calendarMonth - 1; if (m < 0) { setCalendarMonth(11); setCalendarYear(calendarYear - 1); } else setCalendarMonth(m); }}
              className="text-d_text3 hover:text-d_accent text-xs px-1"
            >‹</button>
            <span className="text-[10px] tracking-[1.5px] uppercase text-d_text2 font-semibold">{MONTH_NAMES[calendarMonth]} {calendarYear}</span>
            <button
              onClick={() => { const m = calendarMonth + 1; if (m > 11) { setCalendarMonth(0); setCalendarYear(calendarYear + 1); } else setCalendarMonth(m); }}
              className="text-d_text3 hover:text-d_accent text-xs px-1"
            >›</button>
          </div>
          <div className="grid grid-cols-7 gap-[3px] max-w-[320px]">
            {["M","T","W","T","F","S","S"].map((h, i) => (
              <div key={i} className="text-[9px] text-d_text3 text-center py-0.5 font-semibold">{h}</div>
            ))}
            {Array.from({ length: offset }).map((_, i) => (
              <div key={`emp-${i}`} className="invisible" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayVisits = byDay.get(day) ?? [];
              const isToday = day === TODAY.getDate() && calendarMonth === TODAY.getMonth() && calendarYear === TODAY.getFullYear();
              const isPast = new Date(calendarYear, calendarMonth, day) < new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate());
              const hasVisit = dayVisits.length > 0;
              const primaryDim = hasVisit ? dayVisits[0].dimension : null;
              const isHl = primaryDim && active === primaryDim;

              let bg = "";
              let textColor = "text-d_text2";
              if (isToday) { bg = "bg-d_accent text-black font-bold"; textColor = ""; }
              else if (hasVisit && isPast) { bg = "bg-d_accent/[.12] text-d_accent font-semibold"; textColor = ""; }
              else if (hasVisit) { bg = "bg-d_accent/[.04] text-d_celeste"; textColor = ""; }

              return (
                <button
                  key={day}
                  onMouseEnter={() => primaryDim && setActive(primaryDim)}
                  onMouseLeave={() => setActive(null)}
                  onClick={() => hasVisit && setSelectedVisit(dayVisits[0])}
                  className={`aspect-square rounded-full flex items-center justify-center text-[10px] transition-all max-w-[34px] max-h-[34px] border-2 ${
                    primaryDim ? "" : "border-transparent"
                  } ${bg} ${textColor} ${hasVisit ? "cursor-pointer hover:scale-110" : "cursor-default"} ${isHl ? "ring-2 ring-d_accent" : ""}`}
                  style={primaryDim ? { borderColor: DIM_META[primaryDim].color } : {}}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2.5 flex-wrap mt-3">
            {(Object.keys(DIM_META) as Dimension[]).map((d) => (
              <div key={d} className="flex items-center gap-1">
                <div className="w-[7px] h-[7px] rounded-full border-2" style={{ borderColor: DIM_META[d].color }} />
                <span className="text-[8px] text-d_text3">{DIM_META[d].label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: week list / drill-down */}
        <div className="flex-1 flex flex-col overflow-y-auto min-w-0">
          {selectedVisit ? (
            <div>
              <button
                onClick={() => setSelectedVisit(null)}
                className="text-[10px] text-d_accent bg-d_accent/[.04] border border-d_accent/10 rounded-md px-3 py-1.5 mb-2.5 flex items-center gap-1 hover:bg-d_accent/[.08]"
              >
                ← Back to week
              </button>
              <div className="w-full h-[100px] rounded-[10px] overflow-hidden mb-2.5 relative" style={{
                background: `linear-gradient(135deg,${DIM_META[selectedVisit.dimension].color}15,#0d1b1f)`,
              }}>
                <div className="absolute inset-0 flex items-center justify-center text-5xl opacity-60">{selectedVisit.emoji}</div>
                <div className="absolute inset-0 bg-gradient-to-t from-d_card to-transparent flex items-end p-2.5">
                  <span className="text-2xl mr-2">{selectedVisit.icon}</span>
                  <span className="font-serif text-lg font-semibold">{selectedVisit.name}</span>
                </div>
              </div>
              {[
                ["When", selectedVisit.date.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })],
                ["With", selectedVisit.practitioner],
                ["Where", selectedVisit.location],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 py-1.5 border-b border-d_border/20 text-[11px]">
                  <div className="text-d_text3 w-14">{k}</div>
                  <div className="font-semibold">{v}</div>
                </div>
              ))}
              <div className="flex items-center gap-2 py-1.5 border-b border-d_border/20 text-[11px]">
                <div className="text-d_text3 w-14">Dimension</div>
                <div className="font-semibold" style={{ color: DIM_META[selectedVisit.dimension].color }}>
                  {DIM_META[selectedVisit.dimension].icon} {DIM_META[selectedVisit.dimension].label}
                </div>
              </div>
              <p className="mt-2 text-[11px] text-d_text2 leading-[1.5]">{selectedVisit.description}</p>
            </div>
          ) : (
            <>
              <div className="text-[7px] tracking-[1.8px] uppercase text-d_text3 font-semibold mb-2">This Week's Sessions</div>
              {weekVisits.length === 0 ? (
                <div className="text-[11px] text-d_text3 italic text-center py-6">No sessions booked this week.</div>
              ) : (
                weekVisits.map((v) => (
                  <button
                    key={v.id}
                    onMouseEnter={() => setActive(v.dimension)}
                    onMouseLeave={() => setActive(null)}
                    onClick={() => setSelectedVisit(v)}
                    className="flex items-center gap-2.5 p-2.5 rounded-lg border border-d_border/20 mb-1.5 cursor-pointer transition-all hover:border-d_border_h hover:bg-d_accent/[.012] text-left w-full"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0"
                      style={{ background: `${DIM_META[v.dimension].color}10` }}
                    >
                      {v.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold truncate">{v.name}</div>
                      <div className="text-[9px] text-d_text3">
                        {v.date.toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}
                      </div>
                    </div>
                    <span
                      className="text-[7px] font-semibold tracking-[.4px] px-1.5 py-0.5 rounded ml-auto flex-shrink-0"
                      style={{ background: `${DIM_META[v.dimension].color}22`, color: DIM_META[v.dimension].color }}
                    >
                      {v.dimension}
                    </span>
                  </button>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
